import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from 'currency-symbol-map';
import moment from "moment/moment";
import { useHistory } from "react-router-dom";
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination, Autoplay} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import LockerIcon from '../assets/locker.svg';
import ButtonComponent from "./ButtonComponent";
import { BUTTON_PRIMARY, BUTTON_WARNING, DUROLT_CONTACT_NUMBER, IMAGE_CAROUSEL_AUTOPLAY_DELAY, POD_BANK_TYPE, THREE_HOURS } from "../assets/constants/Constants";
import { CHEVRONS_RIGHT, GOOGLE_MAPS_ARROW_ICON, GOOGLE_MAPS_LOCATION_ICON, SCHEDULE_ICON } from "../assets/constants/Icons";
import { GetAvailableLockerSizesAndRedirectUser, getBookInAdvanceButtonText, getLockerSelectionButtonText, getUnitLabelParams, redirectToGoogleMapsDirections } from "../lib/Utils";
import { getLockerScheduleDetails } from "../api/api";
import ScheduleCalendarComponent from "./ScheduleCalendarComponent";
import CircularProgressLoader from "./CircularProgressLoader";
import CustomDialog from "./CustomDialog";
import { getSecureItemFromSpecificStorage } from "../lib/BrowserStorageAccessMiddleware";
import { LOCAL_STORAGE, LS_DOMAIN_AND_LOCKER_BANK, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, USER_INFO } from "../assets/constants/BrowserStorageKeys";

const LockerDetailsComponent = ({ locker, onBook }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [scheduleDetails, setScheduleDetails] = useState([]);
  const [showScheduleCalendar, setShowScheduleCalendar] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [isBookNowLoading, setIsBookNowLoading] = useState(false);
  const [bookNowLoadingMessage, setBookNowLoadingMessage] = useState('');
  const [userProximityError, setUserProximityError] = useState('');
  const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);

  const {
    name = t("locker_details_default_name"),
    address = t("address_shown_after_booking"),
    fallbackImage = LockerIcon,
    distance = "",
    description = "",
    lockerImages = []
  } = locker || {};

  const imageSlides = Array.isArray(lockerImages) && lockerImages.length > 0
    ? lockerImages
        .map((productImage, index) => ({
          src:
            typeof productImage === "string"
              ? productImage
              : productImage?.url || productImage?.src || productImage?.image || "",
          alt:
            typeof productImage === "string"
              ? `${name} ${index + 1}`
              : productImage?.name || `${name} ${index + 1}`
        }))
        .filter((image) => image.src)
    : [{ src:fallbackImage, alt: name }];

  // Get location string
  const getLocation = () => {
    if (address) return address;
    return "";
  };

  const fetchScheduleDetails = async (date) => {
    setIsLoadingSchedule(true);
    try {
      const response = await getLockerScheduleDetails(locker?.id, date);
      const bookings = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setScheduleDetails(bookings);
    } catch (error) {
      setScheduleDetails([]);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const handleGetScheduleDetails = async () => {
    if (isLoadingSchedule) {
      return;
    }

    await fetchScheduleDetails(moment().format("YYYY-MM-DD"));
    setShowScheduleCalendar(true);
  };

  const handleScheduleDateChange = (date) => {
    if (isLoadingSchedule) {
      return;
    }

    fetchScheduleDetails(date);
  };

  const BookNowHandler = async () => {
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const lockerBankId = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_DOMAIN_AND_LOCKER_BANK)?.lockerBankId;
    const settings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const userId = getSecureItemFromSpecificStorage(LOCAL_STORAGE, USER_INFO)?.id;
    const isAssignmentPricingEnabled = settings?.is_pricing;
    await GetAvailableLockerSizesAndRedirectUser(
      lockerBankInfo,
      lockerBankId,
      userId,
      isAssignmentPricingEnabled,
      history,
      setIsBookNowLoading,
      setBookNowLoadingMessage,
      setUserProximityError,
      setIsUserProximityErrorModalVisible
    );
  }

  return (
    <div className="storage-card">
      
      <div className="image-container">
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={ { clickable: true }}
          loop={imageSlides.length > 1}
          autoplay={imageSlides.length > 1 ? { delay: IMAGE_CAROUSEL_AUTOPLAY_DELAY, disableOnInteraction: false } : false}
          className='image-carousel'
        >
          {imageSlides.map((productImage, index) => (
            <SwiperSlide key={`${productImage.src}-${index}`} className='image-slide'>
              <img
                src={productImage.src}
                alt={productImage.alt}
                className='equipment-image'
                onError={(e) => {
                  e.target.src = fallbackImage;
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      
      </div>

      {/* Content Section */}
      <div className="content">
        <div className="header">
          <h2 className="title">{name}</h2>

          <div className="badge-container">
            <span className="badge badge-open">{t("open_24h_badge")}</span>
            {distance && (
              <span className="badge badge-distance">{distance} km</span>
            )}
          </div>

        </div>

        {description && (
          <div className="description">
            <p>{description}</p>
          </div>
        )}

        <div className="address-section">
          {getLocation() && (
            <p className="address-label">
              <span className="address-icon"><GOOGLE_MAPS_LOCATION_ICON /></span>
              {getLocation()}
            </p>
          )}
          <ButtonComponent
              type={BUTTON_WARNING}
              handleClick={() => redirectToGoogleMapsDirections(locker?.position, locker?.LockerBankURL)}
              title={t('text_google_direction_map')}
              startIcon={<GOOGLE_MAPS_LOCATION_ICON />}
              endIcon={<GOOGLE_MAPS_ARROW_ICON />}
              
            />
            <ButtonComponent
              type={BUTTON_WARNING}
              handleClick={handleGetScheduleDetails}
              title={t('get_schedule_details_text')}
              startIcon={<SCHEDULE_ICON />}
              endIcon={<GOOGLE_MAPS_ARROW_ICON />}
            
            />
          
          <div className="divider"></div>
        </div>
          {locker?.LockerBankType !== POD_BANK_TYPE && 
        <div className="includes">
          <h3 className="subtitle">{t("booking_includes_title")}</h3>

          <div className="feature">
            <span className="feature-icon">✓</span>
            <div className="feature-content">
              <strong>{t("free_cancellation_title")}</strong>
               <p>{t('text_Cancel_UpTo_Three_Hours_Locker', { hours: THREE_HOURS, ...getUnitLabelParams(locker?.LockerBankType, t) })} </p>
            </div>
          </div>
         <div className="feature">
            <span className="feature-icon">💬</span>
            <div className="feature-content">
              <strong>{t("support_24_7_title")}</strong>
              <p>{t("support_24_7_description")}</p>
              <p className="phone-number">{DUROLT_CONTACT_NUMBER}</p>
            </div>
          </div>
        </div>
        }
      </div>
      <div className="footer">
        <div className="price-section" style={{justifyContent:'center'}}>
          <div className="button-group">
            <ButtonComponent type="primary" handleClick={BookNowHandler} title={getLockerSelectionButtonText(locker, t)} />
            <ButtonComponent type="primary" handleClick={onBook} title={getBookInAdvanceButtonText(locker, t)} />
          </div>
        </div>
      </div>

      <ScheduleCalendarComponent
        visible={showScheduleCalendar}
        onClose={() => setShowScheduleCalendar(false)}
        bookings={scheduleDetails}
        lockerName={name}
        onDateChange={handleScheduleDateChange}
        isLoading={isLoadingSchedule}
      />

      {isBookNowLoading && <CircularProgressLoader message={bookNowLoadingMessage} />}

      <CustomDialog
        dialogId='locker-details-user-proximity-error-modal'
        dialogVisible={isUserProximityErrorModalVisible}
        onClose={() => setIsUserProximityErrorModalVisible(false)}
        handleCancel={() => setIsUserProximityErrorModalVisible(false)}
        dialogTitle={t('Location_error')}
        dialogContentText={userProximityError}
        buttonOneTitle={t('button_ok')}
        buttonProps={{ buttonOne: { type: BUTTON_PRIMARY } }}
      />
    </div>
  );
};

export default LockerDetailsComponent;
